import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { customerService } from '../../services/customerService';
import { authService } from '../../authService';
import ResponsiveDataView from '../common/ResponsiveDataView';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Alert,
  Tooltip,
  CircularProgress,
  Avatar
} from '@mui/material';
import {
  Add,
  Search,
  Edit,
  Delete,
  Visibility,
  Download,
  Storage,
  FilterList,
  Sort,
  CheckCircle,
  Cancel,
  PowerSettingsNew,
  LocationOn,
  Notes,
  Close,
  MenuBook
} from '@mui/icons-material';

const CustomersPage = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState('Loading customers...');
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('add');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  // Filters and search
  const [searchTerm, setSearchTerm] = useState('');
  const [filterBarangay, setFilterBarangay] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterDiscount, setFilterDiscount] = useState('all');
  const [sortField, setSortField] = useState('date_added');
  const [sortDirection, setSortDirection] = useState('desc');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;
  
  // Form data
  const [formData, setFormData] = useState({
    customerid: '',
    name: '',
    address: '',
    meterno: '',
    type: '',
    barangay: '',
    discount: 0,
    status: 'Active',
    remarks: ''
  });

  // Stats and filter options
  const [stats, setStats] = useState({ total: 0, active: 0, disconnected: 0 });
  const [barangays, setBarangays] = useState([]);
  const [customerTypes, setCustomerTypes] = useState([]);
  const [discounts, setDiscounts] = useState([]);

  const user = authService.getUserSession();

  const getDefaultCustomerType = () => (customerTypes.length > 0 ? customerTypes[0].type : '');
  const getDefaultBarangay = () => (barangays.length > 0 ? barangays[0].barangay : '');
  const getDefaultDiscount = () => (discounts.length > 0 ? Number(discounts[0].discountpercentage) : 0);

  useEffect(() => {
    loadCustomers();
    loadFilterOptions();
    loadStats();
  }, []);

  useEffect(() => {
    if (customerTypes.length > 0) {
      setFormData((prev) => ({
        ...prev,
        type: prev.type || customerTypes[0].type
      }));
    }
  }, [customerTypes]);

  useEffect(() => {
    if (barangays.length > 0) {
      setFormData((prev) => ({
        ...prev,
        barangay: prev.barangay || barangays[0].barangay
      }));
    }
  }, [barangays]);

  useEffect(() => {
    if (discounts.length > 0) {
      setFormData((prev) => ({
        ...prev,
        discount: prev.discount ?? getDefaultDiscount()
      }));
    }
  }, [discounts]);

  // Debounced search effect
  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
      loadCustomers();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, filterBarangay, filterType, filterStatus, filterDiscount]);

  const loadCustomers = async () => {
    setLoading(true);
    try {
      const result = await customerService.getCustomers({
        search: searchTerm,
        barangay: filterBarangay,
        type: filterType,
        status: filterStatus,
        discount: filterDiscount
      });
      
      if (result.success) {
        setCustomers(result.data || []);
        setError('');
      } else {
        if (result.error.includes('404') || result.error.includes('relation "public.customers" does not exist')) {
          setError('The customers table has not been created yet. Please create the customers table in your Supabase database.');
        } else {
          setError(result.error);
        }
        setCustomers([]);
      }
    } catch (err) {
      console.error('Error loading customers:', err);
      setError('Failed to load customers. Please check your database connection.');
      setCustomers([]);
    }
    setLoading(false);
  };

  const loadFilterOptions = async () => {
    try {
      const [barangayResult, typeResult, discountResult] = await Promise.all([
        customerService.getBarangays(),
        customerService.getCustomerTypes(),
        customerService.getDiscounts()
      ]);

      if (barangayResult.success) setBarangays(barangayResult.data || []);
      if (typeResult.success) setCustomerTypes(typeResult.data || []);
      if (discountResult.success) {
        setDiscounts((discountResult.data || []).map(item => ({
          type: item.type,
          discountpercentage: item.discountpercentage !== null ? Number(item.discountpercentage) : 0
        })));
      }
    } catch (err) {
      console.error('Error loading filter options:', err);
    }
  };

  const loadStats = async () => {
    try {
      const result = await customerService.getCustomerStats();
      if (result.success) {
        setStats(result.data || { total: 0, active: 0, disconnected: 0 });
      }
    } catch (err) {
      console.error('Error loading stats:', err);
    }
  };

  const handleAddCustomer = async () => {
    if (!user) {
      setError('Your session has expired. Please log in again to add customers.');
      return;
    }

    if (customerTypes.length === 0) {
      setError('No customer types are configured. Please add customer types before creating a customer.');
      return;
    }

    if (barangays.length === 0) {
      setError('No barangays are configured. Please add barangays before creating a customer.');
      return;
    }

    const idResult = await customerService.generateCustomerId();
    if (!idResult.success) {
      setError(idResult.error || 'Failed to generate a new customer ID. Please try again.');
      return;
    }

    const { firstname = '', lastname = '' } = user;
    const addedBy = `${firstname} ${lastname}`.trim() || 'Unknown User';

    setFormData({
      customerid: idResult.data,
      name: '',
      address: '',
      meterno: '',
      type: getDefaultCustomerType(),
      barangay: getDefaultBarangay(),
      discount: getDefaultDiscount(),
      status: 'Active',
      remarks: '',
      added_by: addedBy
    });
    setModalType('add');
    setShowModal(true);
  };

  const handleEditCustomer = (customer) => {
    setFormData(customer);
    setSelectedCustomer(customer);
    setModalType('edit');
    setShowModal(true);
  };

  const handleViewCustomer = (customer) => {
    setSelectedCustomer(customer);
    setFormData(customer);
    setModalType('view');
    setShowModal(true);
  };

  const handleStatusChange = (customer) => {
    setSelectedCustomer(customer);
    setFormData({ ...customer, remarks: '' });
    setModalType('status');
    setShowModal(true);
  };

  const handleDeleteCustomer = (customer) => {
    setSelectedCustomer(customer);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (selectedCustomer) {
      const result = await customerService.deleteCustomer(selectedCustomer.customerid);
      if (result.success) {
        loadCustomers();
        loadStats();
        setShowDeleteConfirm(false);
        setSelectedCustomer(null);
      } else {
        setError(result.error);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let result;

    if (modalType === 'add') {
      result = await customerService.addCustomer(formData);
    } else if (modalType === 'edit') {
      result = await customerService.updateCustomer(selectedCustomer.customerid, formData);
    } else if (modalType === 'status') {
      result = await customerService.updateCustomerStatus(
        selectedCustomer.customerid,
        formData.status,
        formData.remarks,
        user.firstname + ' ' + user.lastname
      );
    }

    if (result.success) {
      loadCustomers();
      loadStats();
      loadFilterOptions();
      setShowModal(false);
      setSelectedCustomer(null);
      resetForm();
    } else {
      setError(result.error);
    }
  };

  const resetForm = () => {
    setFormData({
      customerid: '',
      name: '',
      address: '',
      meterno: '',
      type: getDefaultCustomerType(),
      barangay: getDefaultBarangay(),
      discount: getDefaultDiscount(),
      status: 'Active',
      remarks: ''
    });
  };

  const getCustomerInitials = (name) => {
    return name ? name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : '??';
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleExport = () => {
    customerService.exportToCSV(customers);
  };

  const createSampleData = async () => {
    const sampleCustomers = [
      { customerid: 1, name: 'Juan Tolentino', type: 'Residential', barangay: 'Poblacion', discount: 0, status: 'Active', added_by: user.firstname + ' ' + user.lastname },
      { customerid: 2, name: 'Maria Santos', type: 'Commercial', barangay: 'Barangay 1', discount: 5, status: 'Active', added_by: user.firstname + ' ' + user.lastname },
      { customerid: 3, name: 'Pedro Garcia', type: 'Residential', barangay: 'Barangay 2', discount: 0, status: 'Disconnected', added_by: user.firstname + ' ' + user.lastname },
      { customerid: 4, name: 'Ana Cruz', type: 'Residential', barangay: 'Poblacion', discount: 10, status: 'Active', added_by: user.firstname + ' ' + user.lastname },
      { customerid: 5, name: 'Roberto Reyes', type: 'Industrial', barangay: 'Barangay 3', discount: 0, status: 'Active', added_by: user.firstname + ' ' + user.lastname }
    ];

    try {
      for (const customer of sampleCustomers) {
        await customerService.addCustomer(customer);
      }
      loadCustomers();
      loadStats();
      setError('');
    } catch (err) {
      setError('Failed to create sample data: ' + err.message);
    }
  };

  // Memoized sorted and paginated data
  const sortedCustomers = useMemo(() => {
    const sorted = [...customers].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      
      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }
      
      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });
    return sorted;
  }, [customers, sortField, sortDirection]);

  const paginatedCustomers = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return sortedCustomers.slice(startIndex, startIndex + itemsPerPage);
  }, [sortedCustomers, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(sortedCustomers.length / itemsPerPage);

  const StatItem = ({ label, value, icon, color, bgcolor }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, bgcolor: '#ffffff', px: 2, py: 1, borderRadius: 2.5, border: '1px solid #e2e8f0' }}>
      <Box sx={{ color: color, bgcolor: bgcolor, width: 34, height: 34, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {React.cloneElement(icon, { sx: { fontSize: 18 } })}
      </Box>
      <Box>
        <Typography variant="subtitle2" fontWeight={800} color="#0f172a" sx={{ lineHeight: 1.1 }}>
          {value}
        </Typography>
        <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.72rem', fontWeight: 600 }}>
          {label}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
      {/* Top Header & Stat Chips */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 2, mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={800} color="#0f172a" sx={{ letterSpacing: '-0.02em', mb: 0.5 }}>
            Consumer Directory
          </Typography>
          <Stack direction="row" spacing={1.5} sx={{ flexWrap: 'wrap', gap: 1 }}>
            <StatItem label="Total Accounts" value={stats.total} icon={<Visibility />} color="#059669" bgcolor="#ecfdf5" />
            <StatItem label="Active Connections" value={stats.active} icon={<CheckCircle />} color="#0284c7" bgcolor="#e0f2fe" />
            <StatItem label="Disconnected" value={stats.disconnected} icon={<Cancel />} color="#dc2626" bgcolor="#fee2e2" />
          </Stack>
        </Box>
        <Stack direction="row" spacing={1.5}>
          <Button 
            variant="outlined" 
            startIcon={<MenuBook />} 
            onClick={() => navigate('/portal/water-billing/customer-logbook')}
            sx={{ borderRadius: 2.5, borderColor: '#cbd5e1', color: '#059669', bgcolor: '#ffffff', fontWeight: 600, '&:hover': { bgcolor: '#ecfdf5', borderColor: '#059669' } }}
          >
            Print Logbook
          </Button>
          <Button 
            variant="outlined" 
            startIcon={<Download />} 
            onClick={handleExport}
            sx={{ borderRadius: 2.5, borderColor: '#e2e8f0', color: '#475569', fontWeight: 600, '&:hover': { bgcolor: '#ffffff', borderColor: '#cbd5e1' } }}
          >
            Export CSV
          </Button>
          {customers.length === 0 && !loading && (
            <Button 
              variant="outlined" 
              startIcon={<Storage />} 
              onClick={createSampleData}
              sx={{ borderRadius: 2.5, borderColor: '#e2e8f0', color: '#475569', fontWeight: 600 }}
            >
              Sample Data
            </Button>
          )}
          <Button 
            variant="contained" 
            startIcon={<Add />} 
            onClick={handleAddCustomer}
            sx={{ borderRadius: 2.5, bgcolor: '#059669', color: '#ffffff', fontWeight: 600, boxShadow: '0 4px 10px rgba(5, 150, 105, 0.25)', '&:hover': { bgcolor: '#047857' } }}
          >
            Add Consumer
          </Button>
        </Stack>
      </Box>

      {/* Filter and Search Bar */}
      <Card sx={{ mb: 3, p: 2, borderRadius: 3.5, border: '1px solid #e2e8f0', boxShadow: '0 4px 16px -2px rgba(0, 0, 0, 0.04)' }}>
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Search by name, ID, meter or address..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><Search sx={{ color: '#94a3b8' }} /></InputAdornment>,
                sx: { borderRadius: 2.5 }
              }}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Barangay</InputLabel>
              <Select
                value={filterBarangay}
                label="Barangay"
                onChange={(e) => setFilterBarangay(e.target.value)}
                sx={{ borderRadius: 2.5 }}
              >
                <MenuItem value="all">All Barangays</MenuItem>
                {barangays.map(b => (
                  <MenuItem key={b.barangay} value={b.barangay}>{b.barangay}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Classification</InputLabel>
              <Select
                value={filterType}
                label="Classification"
                onChange={(e) => setFilterType(e.target.value)}
                sx={{ borderRadius: 2.5 }}
              >
                <MenuItem value="all">All Types</MenuItem>
                {customerTypes.map(t => (
                  <MenuItem key={t.type} value={t.type}>{t.type}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filterStatus}
                label="Status"
                onChange={(e) => setFilterStatus(e.target.value)}
                sx={{ borderRadius: 2.5 }}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="Disconnected">Disconnected</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Discount</InputLabel>
              <Select
                value={filterDiscount}
                label="Discount"
                onChange={(e) => setFilterDiscount(e.target.value)}
                sx={{ borderRadius: 2.5 }}
              >
                <MenuItem value="all">All Discounts</MenuItem>
                <MenuItem value="none">No Discount</MenuItem>
                {discounts.map(d => (
                  <MenuItem key={d.type} value={String(d.discountpercentage)}>
                    {d.type} ({d.discountpercentage}%)
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2.5 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Customer Directory Responsive Table System */}
      <ResponsiveDataView
        columns={[
          {
            key: 'name',
            label: 'Consumer Name',
            priority: 'high',
            render: (customer) => (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Avatar sx={{ bgcolor: '#ecfdf5', color: '#059669', width: 36, height: 36, fontSize: '0.8125rem', fontWeight: 700 }}>
                  {getCustomerInitials(customer.name)}
                </Avatar>
                <Box>
                  <Typography variant="subtitle2" fontWeight={700} color="#0f172a">
                    {customer.name}
                  </Typography>
                  {customer.discount > 0 && (
                    <Chip label={`${customer.discount}% Disc`} size="small" sx={{ height: 18, fontSize: '0.65rem', bgcolor: '#fef3c7', color: '#92400e', fontWeight: 700 }} />
                  )}
                </Box>
              </Box>
            )
          },
          {
            key: 'customerid',
            label: 'Account ID',
            priority: 'high',
            render: (customer) => (
              <Typography sx={{ fontFamily: 'monospace', fontWeight: 600, color: '#0f172a', fontSize: '0.875rem' }}>
                {customer.customerid}
              </Typography>
            )
          },
          {
            key: 'meterno',
            label: 'Meter No.',
            priority: 'medium',
            render: (customer) => customer.meterno || '—'
          },
          {
            key: 'type',
            label: 'Type',
            priority: 'medium',
            render: (customer) => (
              <Chip label={customer.type} size="small" sx={{ bgcolor: '#f1f5f9', color: '#334155', fontWeight: 600, fontSize: '0.72rem' }} />
            )
          },
          {
            key: 'address',
            label: 'Address',
            priority: 'low',
            render: (customer) => customer.address || '—'
          },
          {
            key: 'barangay',
            label: 'Barangay',
            priority: 'medium'
          },
          {
            key: 'status',
            label: 'Status',
            priority: 'high',
            render: (customer) => (
              <Chip 
                label={customer.status} 
                size="small" 
                sx={{ 
                  bgcolor: customer.status === 'Active' ? '#ecfdf5' : '#fee2e2',
                  color: customer.status === 'Active' ? '#047857' : '#b91c1c',
                  fontWeight: 700,
                  fontSize: '0.72rem'
                }} 
              />
            )
          },
          {
            key: 'date_added',
            label: 'Enrolled',
            priority: 'low',
            render: (customer) => new Date(customer.date_added).toLocaleDateString()
          },
          {
            key: 'actions',
            label: 'Actions',
            priority: 'high',
            align: 'right',
            render: (customer) => (
              <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                <Tooltip title="View Profile">
                  <IconButton size="small" onClick={() => handleViewCustomer(customer)} sx={{ color: '#0284c7' }}>
                    <Visibility fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Edit Customer">
                  <IconButton size="small" onClick={() => handleEditCustomer(customer)} sx={{ color: '#059669' }}>
                    <Edit fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Toggle Status">
                  <IconButton size="small" onClick={() => handleStatusChange(customer)} sx={{ color: '#d97706' }}>
                    <PowerSettingsNew fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton size="small" onClick={() => handleDeleteCustomer(customer)} sx={{ color: '#dc2626' }}>
                    <Delete fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            )
          }
        ]}
        data={paginatedCustomers}
        loading={loading}
        loadingMessage={loadingMessage}
        emptyMessage="No consumer accounts match the search criteria"
        rowKey="customerid"
        paginationSlot={
          <Pagination 
            count={totalPages} 
            page={currentPage} 
            onChange={(e, p) => setCurrentPage(p)} 
            sx={{
              '& .Mui-selected': {
                bgcolor: '#059669 !important',
                color: '#ffffff'
              }
            }}
          />
        }
      />

      {/* Add/Edit/View Modal */}
      <Dialog 
        open={showModal} 
        onClose={() => setShowModal(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3.5, overflow: 'hidden' }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: '#059669', 
          color: '#ffffff', 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          py: 2,
          px: 3
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {modalType === 'add' && <Add />}
            {modalType === 'edit' && <Edit />}
            {modalType === 'view' && <Visibility />}
            {modalType === 'status' && <PowerSettingsNew />}
            <Typography variant="h6" fontWeight={800} color="#ffffff">
              {modalType === 'add' && 'Add New Water Consumer'}
              {modalType === 'edit' && 'Edit Consumer Details'}
              {modalType === 'view' && 'Consumer Profile & Connection'}
              {modalType === 'status' && 'Update Connection Status'}
            </Typography>
          </Box>
          <IconButton onClick={() => setShowModal(false)} sx={{ color: '#ffffff' }}>
            <Close />
          </IconButton>
        </DialogTitle>
        
        <form onSubmit={handleSubmit}>
          <DialogContent dividers sx={{ p: 3, borderColor: '#e2e8f0' }}>
            {modalType === 'view' ? (
              <Grid container spacing={2.5}>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined" sx={{ height: '100%', borderRadius: 2.5, bgcolor: '#f8faf9', borderColor: '#e2e8f0' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Avatar sx={{ width: 48, height: 48, bgcolor: '#059669', color: '#ffffff', mr: 2, fontWeight: 700 }}>
                          {getCustomerInitials(formData.name)}
                        </Avatar>
                        <Box>
                          <Typography variant="h6" fontWeight={800} color="#0f172a">{formData.name}</Typography>
                          <Chip 
                            label={formData.status} 
                            size="small"
                            sx={{ 
                              mt: 0.5, 
                              bgcolor: formData.status === 'Active' ? '#ecfdf5' : '#fee2e2',
                              color: formData.status === 'Active' ? '#047857' : '#b91c1c',
                              fontWeight: 700
                            }}
                          />
                        </Box>
                      </Box>
                      <Stack spacing={1.5}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #e2e8f0', pb: 1 }}>
                          <Typography color="text.secondary" variant="body2">Account ID</Typography>
                          <Typography fontWeight={700} color="#0f172a" fontFamily="monospace">{formData.customerid}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #e2e8f0', pb: 1 }}>
                          <Typography color="text.secondary" variant="body2">Meter No.</Typography>
                          <Typography fontWeight={600} color="#0f172a">{formData.meterno || 'N/A'}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #e2e8f0', pb: 1 }}>
                          <Typography color="text.secondary" variant="body2">Address</Typography>
                          <Typography fontWeight={600} color="#0f172a">{formData.address || 'N/A'}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #e2e8f0', pb: 1 }}>
                          <Typography color="text.secondary" variant="body2">Date Enrolled</Typography>
                          <Typography fontWeight={600} color="#0f172a">
                            {selectedCustomer?.date_added ? new Date(selectedCustomer.date_added).toLocaleDateString() : 'N/A'}
                          </Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined" sx={{ height: '100%', borderRadius: 2.5, borderColor: '#e2e8f0' }}>
                    <CardContent>
                      <Typography variant="subtitle2" fontWeight={700} color="#0f172a" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LocationOn sx={{ color: '#059669', fontSize: 18 }} /> Connection & Classification
                      </Typography>
                      <Stack spacing={1.5} sx={{ mt: 2 }}>
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">Service Location</Typography>
                          <Typography variant="body2" fontWeight={600} color="#0f172a">
                            {formData.address || 'N/A'}, {formData.barangay || 'N/A'}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">Consumer Classification</Typography>
                          <Typography variant="body2" fontWeight={600} color="#0f172a">{formData.type || 'N/A'}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">Applied Discount Rate</Typography>
                          <Typography variant="body2" fontWeight={700} color={formData.discount > 0 ? '#059669' : '#0f172a'}>
                            {formData.discount}%
                          </Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
                {formData.remarks && (
                  <Grid item xs={12}>
                    <Alert severity="info" icon={<Notes />} sx={{ borderRadius: 2.5 }}>
                      <Typography variant="subtitle2" fontWeight={700}>Account Remarks</Typography>
                      <Typography variant="body2">{formData.remarks}</Typography>
                    </Alert>
                  </Grid>
                )}
              </Grid>
            ) : (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Customer ID"
                    value={formData.customerid || 'Auto-Generating...'}
                    disabled
                    size="small"
                    InputProps={{ sx: { fontFamily: 'monospace' } }}
                  />
                </Grid>

                {modalType !== 'status' && (
                  <>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Account Holder Full Name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                        size="small"
                        placeholder="e.g. Juan Dela Cruz"
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Meter Serial No."
                        value={formData.meterno || ''}
                        onChange={(e) => setFormData({ ...formData, meterno: e.target.value })}
                        size="small"
                        placeholder="e.g. MTR-2024-001"
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth required size="small">
                        <InputLabel>Barangay</InputLabel>
                        <Select
                          value={formData.barangay}
                          label="Barangay"
                          onChange={(e) => setFormData({ ...formData, barangay: e.target.value })}
                        >
                          {barangays.map(b => (
                            <MenuItem key={b.barangay} value={b.barangay}>{b.barangay}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Street Address / Purok / Sitio"
                        value={formData.address || ''}
                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                        required
                        size="small"
                        placeholder="e.g. Purok 4, Main Road"
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth required size="small">
                        <InputLabel>Classification Type</InputLabel>
                        <Select
                          value={formData.type}
                          label="Classification Type"
                          onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                        >
                          {customerTypes.map(t => (
                            <MenuItem key={t.type} value={t.type}>{t.type}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Discount Privilege</InputLabel>
                        <Select
                          value={formData.discount}
                          label="Discount Privilege"
                          onChange={(e) => setFormData({ ...formData, discount: Number(e.target.value) })}
                        >
                          {discounts.map(d => (
                            <MenuItem key={d.type} value={d.discountpercentage}>
                              {d.type} ({d.discountpercentage}%)
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </>
                )}

                {modalType === 'status' && (
                  <Grid item xs={12}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Connection Status</InputLabel>
                      <Select
                        value={formData.status}
                        label="Connection Status"
                        onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      >
                        <MenuItem value="Active">Active</MenuItem>
                        <MenuItem value="Disconnected">Disconnected</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Remarks & History Notes"
                    value={formData.remarks}
                    onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                    multiline
                    rows={3}
                    placeholder={modalType === 'status' ? 'Reason for disconnection / reconnection...' : 'Additional notes...'}
                  />
                </Grid>
              </Grid>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2.5, bgcolor: '#f8faf9', justifyContent: 'space-between' }}>
            <Button 
              onClick={() => setShowModal(false)} 
              variant="outlined" 
              sx={{ borderRadius: 2, borderColor: '#e2e8f0', color: '#475569', fontWeight: 600 }}
            >
              Close
            </Button>
            {modalType !== 'view' && (
              <Button 
                type="submit" 
                variant="contained"
                startIcon={modalType === 'add' ? <Add /> : <Edit />}
                sx={{ borderRadius: 2, bgcolor: '#059669', color: '#ffffff', fontWeight: 600, '&:hover': { bgcolor: '#047857' } }}
              >
                {modalType === 'add' ? 'Save Consumer Account' : 'Save Changes'}
              </Button>
            )}
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={showDeleteConfirm} 
        onClose={() => setShowDeleteConfirm(false)}
        PaperProps={{ sx: { borderRadius: 3.5, p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: '#dc2626' }}>Confirm Removal</DialogTitle>
        <DialogContent>
          <Typography color="#475569">
            Are you sure you want to remove consumer account <strong style={{ color: '#0f172a' }}>{selectedCustomer?.name}</strong>?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setShowDeleteConfirm(false)} sx={{ borderRadius: 2, color: '#64748b', fontWeight: 600 }}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained" sx={{ borderRadius: 2, fontWeight: 600 }}>Delete Account</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CustomersPage;
